import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import workflowAddConditionalTextStep1 from '~/ui/assets/images/workflow-add-conditional-text-step-1.svg';
import workflowAddConditionalTextStep2 from '~/ui/assets/images/workflow-add-conditional-text-step-2.svg';
import workflowAddConditionalTextStep3 from '~/ui/assets/images/workflow-add-conditional-text-step-3.svg';
import workflowAddConditionalText from '~/ui/assets/images/workflow-add-conditional-text.svg';
import workflowAddDocumentFieldsStep1 from '~/ui/assets/images/workflow-add-document-fields-step-1.svg';
import workflowAddDocumentFieldsStep2 from '~/ui/assets/images/workflow-add-document-fields-step-2.svg';
import workflowAddDocumentFieldsStep3 from '~/ui/assets/images/workflow-add-document-fields-step-3.svg';
import workflowAddDocumentFields from '~/ui/assets/images/workflow-add-document-fields.svg';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';

import FieldCard from './FieldCard';
import FieldCardDetail from './FieldCardDetail';

const steps = {
  DEFAULT: 'DEFAULT',
  DOCUMENT_FIELDS: 'DOCUMENT_FIELDS',
  CONDITIONAL_TEXT: 'DOCUMENT_CONDITIONAL_TEXTFIELDS',
};

function TemplateSteps() {
  const intl = useIntl();
  const [step, setStep] = useState(steps.DEFAULT);

  function backToDefault() {
    setStep(steps.DEFAULT);
  }

  switch (step) {
    case steps.DOCUMENT_FIELDS:
      return (
        <FieldCardDetail
          bg="#DBF4F0"
          title={intl.formatMessage({
            id: 'TemplateSteps.addingDocumentFieldsTitle',
            defaultMessage: 'Adding Document Fields',
            description: 'Title for adding document fields tutorial',
          })}
          steps={[
            {
              title: intl.formatMessage({
                id: 'TemplateSteps.prepareDocumentStepTitle',
                defaultMessage: 'Prepare document',
                description: 'Title for prepare document step',
              }),
              description: intl.formatMessage({
                id: 'TemplateSteps.prepareDocumentStepDescription',
                defaultMessage:
                  'Mark each variable directly in the document with \\{\\{\\}\\}.',
                description: 'Description for prepare document step',
              }),
              imgSrc: workflowAddDocumentFieldsStep1,
            },
            {
              title: intl.formatMessage({
                id: 'TemplateSteps.uploadDocumentStepTitle',
                defaultMessage: 'Upload document',
                description: 'Title for upload document step',
              }),
              description: intl.formatMessage({
                id: 'TemplateSteps.uploadDocumentStepDescription',
                defaultMessage:
                  'Evisort saves the position and name for each marked variable.',
                description: 'Description for upload document step',
              }),
              imgSrc: workflowAddDocumentFieldsStep2,
            },
            {
              title: intl.formatMessage({
                id: 'TemplateSteps.setUpFieldsStepTitle',
                defaultMessage: 'Set up fields',
                description: 'Title for set up fields step',
              }),
              description: intl.formatMessage({
                id: 'TemplateSteps.setUpFieldsStepDescription',
                defaultMessage:
                  'Specify the type of information expected and its structure.',
                description: 'Description for set up fields step',
              }),
              imgSrc: workflowAddDocumentFieldsStep3,
            },
          ]}
          onClose={backToDefault}
        />
      );
    case steps.CONDITIONAL_TEXT:
      return (
        <FieldCardDetail
          bg="#D7CDFF"
          title={intl.formatMessage({
            id: 'TemplateSteps.addingConditionalTextTitle',
            defaultMessage: 'Adding Conditional Text',
            description: 'Title for adding conditional text tutorial',
          })}
          steps={[
            {
              title: intl.formatMessage({
                id: 'TemplateSteps.prepareDocumentStepTitle',
                defaultMessage: 'Prepare document',
                description: 'Title for prepare document step',
              }),
              description: intl.formatMessage({
                id: 'TemplateSteps.prepareConditionalTextStepDescription',
                defaultMessage:
                  'Mark each block of conditional text with \\{\\{name\\}\\}.[[conditional content]].',
                description: 'Description for prepare conditional text step',
              }),
              imgSrc: workflowAddConditionalTextStep1,
            },
            {
              title: intl.formatMessage({
                id: 'TemplateSteps.uploadDocumentStepTitle',
                defaultMessage: 'Upload document',
                description: 'Title for upload document step',
              }),
              description: intl.formatMessage({
                id: 'TemplateSteps.uploadConditionalTextStepDescription',
                defaultMessage:
                  'Evisort saves the position and name of each marked block of text.',
                description: 'Description for upload conditional text step',
              }),
              imgSrc: workflowAddConditionalTextStep2,
            },
            {
              title: intl.formatMessage({
                id: 'TemplateSteps.setUpConditionalTextStepTitle',
                defaultMessage: 'Set up conditional text',
                description: 'Title for set up conditional text step',
              }),
              description: intl.formatMessage({
                id: 'TemplateSteps.setUpConditionalTextStepDescription',
                defaultMessage:
                  'Specify the rules for when the conditional content is included in the generated document.',
                description: 'Description for set up conditional text step',
              }),
              imgSrc: workflowAddConditionalTextStep3,
            },
          ]}
          onClose={backToDefault}
        />
      );
    case steps.DEFAULT:
    default:
      return (
        <FlexLayout space={6}>
          <FieldCard
            description={intl.formatMessage({
              id: 'TemplateSteps.documentFieldsDescription',
              defaultMessage:
                "Designate placeholders in the document for the insertion of custom text or variable values based on information provided by the submitter in the intake form. You'll choose which intake form answer to tie the placeholders to in a later step.",
              description: 'Description for document fields card',
            })}
            id="workflow--adding-document-fields-image"
            imgSrc={workflowAddDocumentFields}
            title={intl.formatMessage({
              id: 'TemplateSteps.addingDocumentFieldsTitle',
              defaultMessage: 'Adding Document Fields',
              description: 'Title for adding document fields tutorial',
            })}
            onClick={() => setStep(steps.DOCUMENT_FIELDS)}
          />
          <FieldCard
            description={intl.formatMessage({
              id: 'TemplateSteps.conditionalTextDescription',
              defaultMessage:
                'Designate areas of the uploaded document such as clauses and exhibits that may be but will not always be included in the generated document. You will specify the logic for when the text should be included at a later step.',
              description: 'Description for conditional text card',
            })}
            id="workflow--adding-condition-text-image"
            imgSrc={workflowAddConditionalText}
            title={intl.formatMessage({
              id: 'TemplateSteps.addingConditionalTextTitle',
              defaultMessage: 'Adding Conditional Text',
              description: 'Title for adding conditional text tutorial',
            })}
            onClick={() => setStep(steps.CONDITIONAL_TEXT)}
          />
        </FlexLayout>
      );
  }
}

export default TemplateSteps;
